import {useRef} from "react";
import type {TFunction} from "i18next";
import type {WebRtcClient} from "../../webrtc/WebRtcClient";
import type {AppNotificationCreateAttributes} from "../NotificationContext";
import {isDisconnectRejection} from "../../ws/WsClient";
import type {TrackType} from "@inpriton/types/webrtcTypes";

const usePublish = ({
  stream, videoStream, webrtc, createNotification, hideNotification, t,
}: {
  stream: MediaStream
  videoStream: MediaStream
  webrtc: WebRtcClient
  createNotification: (attributes: AppNotificationCreateAttributes) => string
  hideNotification: (id: string) => void
  t: TFunction
}) => {
  const publishLocalMediaRef = useRef<() => Promise<void>>(async () => {})

  // RISKS.md/P2.15: which local kinds are failing to reach the server. A set rather than a flag:
  // the two publish from different chains, so one recovering must not retract the other's report.
  const publishFailedRef = useRef<Set<TrackType>>(new Set())

  // Derives the copy from the whole set, so a camera-only failure never claims the microphone died.
  // Not `notifyWsFailure`: this also runs on the clear path, with no rejection, to narrow or retract.
  const syncPublishToast = () => {
    const failed = publishFailedRef.current
    // First-wins de-dupe: retract before re-raising, or a narrowed message is dropped (P2.44).
    hideNotification('publish-failed')
    if (!failed.size) return
    createNotification({
      id: 'publish-failed',
      message: t(failed.has('video') && failed.has('audio') ? 'chat.publishFailed'
        : failed.has('video') ? 'chat.publishFailedCamera'
          : 'chat.publishFailedMicrophone'),
      persistent: true,
      actions: [{ label: t('lobby.retry'), onClick: () => { void publishLocalMediaRef.current() } }],
    })
  }

  const reportPublishFailure = (kinds: TrackType[], e: unknown) => {
    console.warn('publishing local media failed:', kinds.join('+'), e)
    if (isDisconnectRejection(e)) return
    kinds.forEach(kind => publishFailedRef.current.add(kind))
    syncPublishToast()
  }

  // `filter`, not `some`: `some` short-circuits on the first delete and leaves the rest of the set.
  const clearPublishFailure = (kinds: TrackType[]) => {
    if (kinds.filter(kind => publishFailedRef.current.delete(kind)).length) syncPublishToast()
  }

  // RISKS.md/P2.30: video from `videoStream` (the filter's output), audio from `stream` (the raw
  // capture). Handed to createSender uncalled and never as a snapshot: it reads each kind after
  // init() (RISKS.md/P1.36), and both streams are mutated in place.
  const localPublishSet = () => new MediaStream([
    ...stream.getAudioTracks(),
    ...videoStream.getVideoTracks(),
  ])

  // RISKS.md/P1.23: publishing must NEVER fail the join — the server has already accepted it, and
  // a participant sending nothing can still see and hear everyone. So this reports and resolves.
  // `createSender` stops at the first failing track, so both kinds are reported and the retry
  // republishes both; both sides supersede last-to-produce, which is what makes that safe (P2.60).
  const publishLocalMedia = async () => {
    const kinds: TrackType[] = ['audio', 'video']
    try {
      clearPublishFailure(await webrtc.createSender(localPublishSet, 'camera'))
    } catch (e) {
      reportPublishFailure(kinds, e)
    }
  }

  publishLocalMediaRef.current = publishLocalMedia

  return { publishLocalMedia, reportPublishFailure, clearPublishFailure }
}

export default usePublish
